interface Props {
  /** Рейтинг от 0 до 5 (включая дробные значения 4.3, 3.7 и т.п.). */
  rating: number | string | null;
  /** Показывать ли числовое значение справа от звёздочек. */
  showValue?: boolean;
  /** Размер звёздочек в пикселях. */
  size?: number;
  /** Количество отзывов (если задано — показывается в скобках). */
  reviewsCount?: number | null;
}

/**
 * Звёздочный рейтинг 0..5. Поддерживает дробные значения через partial-fill
 * звезды (clip-path 50%). Принимает строку (от API: '4.30') или число.
 *
 * Accessibility: aria-label с явным текстом «Рейтинг 4.3 из 5».
 */
export function Stars({ rating, showValue = true, size = 16, reviewsCount }: Props) {
  if (rating === null || rating === undefined || rating === '') {
    return (
      <span className="stars stars--none" aria-label="Рейтинг не определён" style={{ color: 'var(--muted, #6b7287)', fontSize: size - 2 }}>
        нет рейтинга
      </span>
    );
  }
  const value = typeof rating === 'string' ? Number(rating) : rating;
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  const clamped = Math.max(0, Math.min(5, value));
  const full = Math.floor(clamped);
  const partial = clamped - full;
  const empty = 5 - full - (partial > 0 ? 1 : 0);

  return (
    <span
      className="stars"
      role="img"
      aria-label={`Рейтинг ${clamped.toFixed(1)} из 5${
        reviewsCount ? `, ${reviewsCount} отзывов` : ''
      }`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <span style={{ display: 'inline-flex', color: '#e0c060', fontSize: size, lineHeight: 1 }}>
        {Array.from({ length: full }).map((_, i) => (
          <span key={`f${i}`}>★</span>
        ))}
        {partial > 0 && (
          <span
            style={{
              position: 'relative',
              display: 'inline-block',
              color: 'var(--border, #e6e2d6)',
            }}
          >
            ★
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                color: '#e0c060',
                width: `${partial * 100}%`,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              ★
            </span>
          </span>
        )}
        {Array.from({ length: empty }).map((_, i) => (
          <span key={`e${i}`} style={{ color: 'var(--border, #e6e2d6)' }}>
            ★
          </span>
        ))}
      </span>
      {showValue && (
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: size - 2,
            color: 'var(--text, #0e1320)',
            fontWeight: 600,
          }}
        >
          {clamped.toFixed(1)}
        </span>
      )}
      {reviewsCount !== null && reviewsCount !== undefined && (
        <span style={{ color: 'var(--muted, #6b7287)', fontSize: size - 2 }}>
          ({reviewsCount.toLocaleString('ru-RU')})
        </span>
      )}
    </span>
  );
}
